import { useMemo, useState } from "react";
import {
  ACCELERANT_CATEGORY_LABELS,
  COMPOUND_CLASS_LABELS,
  COMPOUND_DATABASE,
  type CompoundEntry,
} from "@/data/compoundDatabase";
import { useDataSources } from "@/hooks/useDataSources";
import { useReferenceCompounds } from "@/hooks/useReferenceCompounds";
import type { ReferenceCompoundItem } from "@/types/referenceCompound";
import NFPADiamond from "./NFPADiamond";

type FilterType = "all" | "accelerant" | "aromatic" | "pah" | "other";

/**
 * 인화성 액체(ILR) 지시성분 여부.
 *
 * 이 화면은 API 항목과 정적 fallback 항목을 섞어 쓰는데 **필드 이름이 다르다**:
 * API 는 `is_ignitable_liquid_indicator`, 정적 DB 는 `is_fire_accelerant`.
 * 종전에는 후자만 봐서 **API 데이터에서는 필터가 언제나 0건**이었다
 * (2026-08-20 교차 리뷰). 값이 없으면 "기록 없음" — true 로 치지 않는다.
 */
function isIgnitableIndicator(c: ReferenceCompoundItem): boolean {
  return c.is_ignitable_liquid_indicator ?? c.is_fire_accelerant ?? false;
}

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "accelerant", label: "인화성 지시성분" },
  { value: "aromatic", label: "방향족" },
  { value: "pah", label: "PAH" },
  { value: "other", label: "기타" },
];

function matchesFilter(
  compound: ReferenceCompoundItem,
  filter: FilterType,
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "accelerant":
      return isIgnitableIndicator(compound);
    case "aromatic":
      return compound.compound_class === "aromatic_hydrocarbon";
    case "pah":
      return compound.compound_class === "pah";
    case "other":
      return (
        compound.compound_class !== "aromatic_hydrocarbon" &&
        compound.compound_class !== "pah" &&
        !isIgnitableIndicator(compound)
      );
  }
}

function truncate(str: string, len: number): string {
  return str.length > len ? `${str.slice(0, len)}...` : str;
}

/** 정적 CompoundEntry를 ReferenceCompoundItem으로 변환 (레거시 호환) */
function staticToItem(c: CompoundEntry, idx: number): ReferenceCompoundItem {
  return {
    id: -(idx + 1),
    compound_name: c.compound_name,
    compound_name_ko: c.compound_name_ko,
    cas_number: c.cas_number,
    smiles: c.smiles,
    molecular_formula: c.molecular_formula,
    compound_class: c.compound_class,
    is_fire_accelerant: c.is_fire_accelerant,
    accelerant_category: c.accelerant_category,
    flash_point_celsius: c.flash_point_celsius,
    nfpa: c.nfpa,
    ghs: c.ghs,
    hazards: c.hazards,
    precautions: c.precautions,
    data_source_id: null,
    data_source: null,
    created_at: new Date().toISOString(),
  };
}

export default function ReferenceDBExplorer() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedDataSourceId, setSelectedDataSourceId] = useState<
    number | null
  >(null);

  const { data: dataSources = [] } = useDataSources();

  const { data: apiCompounds, isSuccess: apiLoaded } = useReferenceCompounds({
    data_source_id: selectedDataSourceId ?? undefined,
    search: search || undefined,
    limit: 200,
  });

  // API 데이터 사용. 출처 미선택이고 API에 데이터 없으면 정적 fallback 병합
  const allCompounds = useMemo<ReferenceCompoundItem[]>(() => {
    if (apiLoaded && apiCompounds && apiCompounds.length > 0) {
      return apiCompounds;
    }
    // 출처 필터 선택 시 fallback 없음 (API 응답만 신뢰)
    if (selectedDataSourceId !== null) {
      return apiCompounds ?? [];
    }
    // fallback: 정적 DB 변환
    return COMPOUND_DATABASE.map(staticToItem);
  }, [apiLoaded, apiCompounds, selectedDataSourceId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return allCompounds.filter((c) => {
      if (!matchesFilter(c, filter)) return false;
      // 검색은 API가 처리하거나 (apiLoaded), 클라이언트 필터링
      if (apiLoaded || !q) return true;
      return (
        c.compound_name.toLowerCase().includes(q) ||
        (c.compound_name_ko?.toLowerCase().includes(q) ?? false) ||
        (c.smiles?.toLowerCase().includes(q) ?? false) ||
        (c.molecular_formula?.toLowerCase().includes(q) ?? false) ||
        (c.cas_number?.includes(q) ?? false)
      );
    });
  }, [allCompounds, filter, search, apiLoaded]);

  const totalLabel = apiLoaded
    ? `${filtered.length}건 (DB: ${allCompounds.length}건)`
    : `${COMPOUND_DATABASE.length}종 (정적 DB)`;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <div className="w-2 h-2 bg-orange-500 rounded-full" />
        참조 화합물 DB 탐색기
        <span className="text-sm font-normal text-gray-500">
          ({totalLabel})
        </span>
      </h3>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <input
          type="text"
          placeholder="화합물명, SMILES, 분자식, CAS 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        />
        {/* 출처 드롭다운 */}
        <select
          value={selectedDataSourceId ?? ""}
          onChange={(e) =>
            setSelectedDataSourceId(
              e.target.value === "" ? null : Number(e.target.value),
            )
          }
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white min-w-36"
        >
          <option value="">전체 출처</option>
          {dataSources.map((ds) => (
            <option key={ds.id} value={ds.id}>
              {ds.name}
            </option>
          ))}
        </select>
      </div>

      {/* 분류 필터 버튼 */}
      <div className="flex gap-1 flex-wrap mb-4">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setFilter(opt.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === opt.value
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="text-xs text-gray-500 mb-3">{filtered.length}건 표시 중</p>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left py-2 px-2 text-gray-600 font-medium w-8">
                #
              </th>
              <th className="text-left py-2 px-2 text-gray-600 font-medium">
                화합물명
              </th>
              <th className="text-left py-2 px-2 text-gray-600 font-medium">
                분자식
              </th>
              <th className="text-left py-2 px-2 text-gray-600 font-medium hidden md:table-cell">
                SMILES
              </th>
              <th className="text-left py-2 px-2 text-gray-600 font-medium hidden sm:table-cell">
                분류
              </th>
              <th className="text-left py-2 px-2 text-gray-600 font-medium hidden lg:table-cell">
                출처
              </th>
              <th className="text-right py-2 px-2 text-gray-600 font-medium hidden lg:table-cell">
                인화점
              </th>
              <th className="text-center py-2 px-2 text-gray-600 font-medium hidden lg:table-cell">
                NFPA
              </th>
              <th className="text-center py-2 px-2 text-gray-600 font-medium">
                인화성 지시
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((compound, idx) => {
              const isExpanded = expandedId === compound.id;
              return (
                <CompoundRow
                  key={`${compound.cas_number ?? ""}-${compound.id}`}
                  compound={compound}
                  index={idx + 1}
                  isExpanded={isExpanded}
                  onToggle={() =>
                    setExpandedId(isExpanded ? null : compound.id)
                  }
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-gray-500 py-8 text-sm">
          검색 결과가 없습니다.
        </p>
      )}
    </div>
  );
}

function CompoundRow({
  compound,
  index,
  isExpanded,
  onToggle,
}: {
  compound: ReferenceCompoundItem;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const sourceName = compound.data_source?.name ?? "정적 DB";
  const isRestricted =
    compound.data_source?.security_level === "restricted" ||
    compound.data_source?.security_level === "classified";

  return (
    <>
      <tr
        className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="py-2 px-2 text-gray-400 text-xs">{index}</td>
        <td className="py-2 px-2">
          <div className="font-medium text-gray-900">
            {compound.compound_name_ko ?? compound.compound_name}
          </div>
          {compound.compound_name !== compound.compound_name_ko && (
            <div className="text-xs text-gray-500">
              {compound.compound_name}
            </div>
          )}
        </td>
        <td className="py-2 px-2 text-gray-600 font-mono text-xs">
          {compound.molecular_formula ?? "-"}
        </td>
        <td className="py-2 px-2 text-gray-500 font-mono text-xs hidden md:table-cell">
          {compound.smiles ? truncate(compound.smiles, 20) : "-"}
        </td>
        <td className="py-2 px-2 hidden sm:table-cell">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {compound.compound_class
              ? (COMPOUND_CLASS_LABELS[compound.compound_class] ??
                compound.compound_class)
              : "-"}
          </span>
        </td>
        <td className="py-2 px-2 hidden lg:table-cell">
          {isRestricted ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              {sourceName}
            </span>
          ) : (
            <span className="text-xs text-gray-500">{sourceName}</span>
          )}
        </td>
        <td className="py-2 px-2 text-right text-gray-600 hidden lg:table-cell">
          {compound.flash_point_celsius != null
            ? `${compound.flash_point_celsius}°C`
            : "-"}
        </td>
        <td className="py-2 px-2 text-center hidden lg:table-cell">
          {compound.nfpa ? (
            <NFPADiamond
              health={compound.nfpa.health}
              flammability={compound.nfpa.flammability}
              instability={compound.nfpa.instability}
              size={36}
            />
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
        <td className="py-2 px-2 text-center">
          {isIgnitableIndicator(compound) ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              인화성 지시
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              -
            </span>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-blue-50">
          <td colSpan={9} className="px-4 py-3">
            <ExpandedDetail compound={compound} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetail({ compound }: { compound: ReferenceCompoundItem }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
      {/* Basic Info */}
      <div>
        <h5 className="font-medium text-gray-700 mb-2">기본 정보</h5>
        <dl className="space-y-1">
          <div className="flex">
            <dt className="text-gray-500 w-20">CAS</dt>
            <dd className="text-gray-900 font-mono">
              {compound.cas_number ?? "-"}
            </dd>
          </div>
          <div className="flex">
            <dt className="text-gray-500 w-20">SMILES</dt>
            <dd className="text-gray-900 font-mono text-xs break-all">
              {compound.smiles ?? "-"}
            </dd>
          </div>
          <div className="flex">
            <dt className="text-gray-500 w-20">인화점</dt>
            <dd className="text-gray-900">
              {compound.flash_point_celsius != null
                ? `${compound.flash_point_celsius}°C`
                : "-"}
            </dd>
          </div>
          {compound.accelerant_category && (
            <div className="flex">
              <dt className="text-gray-500 w-20">제품군</dt>
              <dd className="text-gray-900">
                {ACCELERANT_CATEGORY_LABELS[compound.accelerant_category] ??
                  compound.accelerant_category}
              </dd>
            </div>
          )}
          <div className="flex">
            <dt className="text-gray-500 w-20">출처</dt>
            <dd className="text-gray-900">
              {compound.data_source
                ? `${compound.data_source.name} (${compound.data_source.organization ?? ""})`
                : "정적 DB"}
            </dd>
          </div>
        </dl>
      </div>

      {/* NFPA & GHS */}
      <div>
        <h5 className="font-medium text-gray-700 mb-2">NFPA / GHS</h5>
        {compound.nfpa ? (
          <div className="flex items-start gap-3 mb-2">
            <NFPADiamond
              health={compound.nfpa.health}
              flammability={compound.nfpa.flammability}
              instability={compound.nfpa.instability}
              size={48}
            />
            <div className="text-xs space-y-0.5">
              <p>
                <span className="text-blue-600">건강:</span>{" "}
                {compound.nfpa.health}
              </p>
              <p>
                <span className="text-red-600">인화성:</span>{" "}
                {compound.nfpa.flammability}
              </p>
              <p>
                <span className="text-yellow-600">불안정:</span>{" "}
                {compound.nfpa.instability}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-400">NFPA 데이터 없음</p>
        )}
        <div className="flex flex-wrap gap-1">
          {(compound.ghs ?? []).map((code) => (
            <span
              key={code}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-orange-100 text-orange-700"
            >
              {code}
            </span>
          ))}
        </div>
      </div>

      {/* Hazards & Precautions */}
      <div>
        <h5 className="font-medium text-gray-700 mb-2">위험성 / 예방조치</h5>
        <div className="space-y-2">
          <div>
            <p className="text-xs text-red-600 font-medium mb-1">위험성</p>
            <ul className="text-xs text-gray-700 space-y-0.5">
              {(compound.hazards ?? []).length > 0 ? (
                (compound.hazards ?? []).map((h) => <li key={h}>- {h}</li>)
              ) : (
                <li className="text-gray-400">-</li>
              )}
            </ul>
          </div>
          <div>
            <p className="text-xs text-blue-600 font-medium mb-1">예방조치</p>
            <ul className="text-xs text-gray-700 space-y-0.5">
              {(compound.precautions ?? []).length > 0 ? (
                (compound.precautions ?? []).map((p) => <li key={p}>- {p}</li>)
              ) : (
                <li className="text-gray-400">-</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
